import React, { memo, useMemo } from 'react';

import { Box, Typography } from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { jsonStringify } from 'utils/json';

import CitedText from '../../../../AiActivityCitations/CitedText';
import FormFieldDiffTable from '../FormFieldDiffTable';
import { resolveFieldDisplay } from '../formReview.utils';
import { FORM_REVIEW_LABELS, FormReviewTestIds } from '../FormReviewCard.constants';
import { tableCellStyles, tableRowStyles, tableStyles } from '../FormReviewCard.styles';

const formatCell = value => {
  if (value === null || value === undefined || value === '') {
    return FORM_REVIEW_LABELS.NO_VALUE;
  }

  if (_.isObject(value)) {
    return jsonStringify(value);
  }

  return String(value);
};

const FormFieldValue = ({ field, schemaField, tableDiff }) => {
  const display = useMemo(() => resolveFieldDisplay(field, schemaField), [field, schemaField]);
  const itemFields = schemaField?.itemFields ?? [];
  const diffRows = tableDiff?.rows ?? null;

  if (display.isTable) {
    if (diffRows && diffRows.length > 0) {
      return <FormFieldDiffTable rows={diffRows} />;
    }

    if (display.rows.length === 0) {
      return (
        <Typography data-testid={FormReviewTestIds.FIELD_VALUE} variant="regular14">
          {FORM_REVIEW_LABELS.NO_VALUE}
        </Typography>
      );
    }

    return (
      <Box data-testid={FormReviewTestIds.FIELD_TABLE} sx={tableStyles}>
        <Box sx={tableRowStyles}>
          {itemFields.map(itemField => (
            <Box key={itemField.key} sx={tableCellStyles}>
              <Typography variant="bold12">{itemField.label ?? itemField.key}</Typography>
            </Box>
          ))}
        </Box>
        {display.rows.map((row, rowIndex) => (
          // eslint-disable-next-line react/no-array-index-key
          <Box key={rowIndex} sx={tableRowStyles}>
            {itemFields.map(itemField => (
              <Box key={itemField.key} sx={tableCellStyles}>
                <Typography variant="regular12">
                  <CitedText text={formatCell(row?.[itemField.key])} />
                </Typography>
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Typography data-testid={FormReviewTestIds.FIELD_VALUE} variant="regular14">
      <CitedText text={display.text} />
    </Typography>
  );
};

FormFieldValue.propTypes = {
  field: PropTypes.shape({
    value: PropTypes.string,
    overriddenValue: PropTypes.string,
    reviewState: PropTypes.string,
  }),
  schemaField: PropTypes.shape({
    type: PropTypes.string,
    itemFields: PropTypes.array,
  }),
  tableDiff: PropTypes.shape({
    fieldKey: PropTypes.string,
    rows: PropTypes.array,
  }),
};

export default memo(FormFieldValue);
